/**
 * Route Guards — proteção de rotas (UX only, spec §11.9)
 *
 * IMPORTANTE: os guards aqui são APENAS para UX (login quando não há sessão; tela de sem
 * acesso quando falta permissão — #828).
 * A autorização real (RBAC) é responsabilidade do backend Go.
 * O frontend NÃO deve confiar em roles locais para esconder funcionalidades críticas.
 *
 * Code-splitting por permissão: bundles de módulos restritos (admin, financeiro, crédito)
 * são carregados via lazy() apenas quando o usuário tem a role necessária.
 * Isso evita que o bundle seja baixado para usuários sem acesso (reduz superfície e tamanho).
 */

import { Navigate, useLocation, Outlet } from 'react-router-dom'
import { useAuth } from '@/features/auth/auth-context'
import { temPermissao } from '@/features/auth/permissions'
import { ForbiddenState } from '@/features/shared/forbidden-state'

export function VerificandoSessao() {
  return (
    <div
      className="flex min-h-screen items-center justify-center"
      role="status"
      aria-label="Verificando autenticação..."
    >
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
    </div>
  )
}

// ─── Guard: requer autenticação ───────────────────────────────────────────────

export function RequireAuth() {
  const { isAuthenticated, isLoading } = useAuth()
  const location = useLocation()

  if (isLoading) return <VerificandoSessao />

  if (!isAuthenticated) {
    // Preserva a URL destino para redirecionar após o login
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  return <Outlet />
}

// ─── Guard: requer permissão RBAC (UX; o backend segue sendo a fronteira) ─────

export function RequirePermission({ permissoes, children }: { permissoes: readonly string[]; children?: React.ReactNode }) {
  const { user } = useAuth()
  if (!permissoes.some((p) => temPermissao(user?.perfis, p))) return <ForbiddenState />
  return <>{children ?? <Outlet />}</>
}

// ─── Guard: requer role específica ────────────────────────────────────────────

interface RequireRoleProps {
  roles: string[]
  fallback?: React.ReactNode
}

export function RequireRole({ roles, fallback }: RequireRoleProps) {
  const { user } = useAuth()

  // `user.role` é o claim JWT padrão do Supabase (sempre "authenticated") —
  // NUNCA o papel RBAC do PPM. Os perfis reais (admin/operador/...) vêm de
  // `user.perfis`, resolvidos do banco via GET /bff/auth/me (auth-context.tsx).
  if (!user || !user.perfis.some((perfil) => roles.includes(perfil))) {
    if (fallback) return <>{fallback}</>
    return <Navigate to="/sem-acesso" replace />
  }

  return <Outlet />
}
